import * as React from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { LAYOUT } from './layout';
import { RotatePrompt } from './RotatePrompt';

/*
 * Stage — the single scaling mechanism for the board.
 *
 * Renders a fixed design-size box (LAYOUT.design) and scales it with
 * transform:scale(min(vw/W, vh/H)) so it always fits the viewport, in either
 * orientation. Everything inside positions in absolute *design* pixels; the one
 * scale factor maps design px → screen px, keeping every relationship (a fan
 * around an avatar, a card over the table center) rigid.
 *
 * The fit is never clamped: a floor on the scale would let the board overflow
 * the viewport and get clipped by the wrapper. On a portrait phone the honest
 * fit is small, so RotatePrompt asks for landscape on top of it.
 */

export const Stage: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  const { w, h } = LAYOUT.design;

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const recompute = () => {
      setScale(Math.min(wrapper.clientWidth / w, wrapper.clientHeight / h));
    };

    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [w, h]);

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100dvw',
        height: '100dvh',
        overflow: 'hidden',
        background: 'var(--color-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        className="stage"
        style={{
          position: 'relative',
          width: w,
          height: h,
          flex: 'none',
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
        }}
      >
        {children}
      </div>

      <RotatePrompt />
    </div>
  );
};

export default Stage;
